// Адаптер над Vuex-модулем `@emcd-shared/coin-service`.
//
// Воспроизводит старую форму данных pinia-стора `user.js`
// (coins / config.coins / cryptoInFiat / …), чтобы не переписывать шаблоны.
// Единственный источник правды по монетам/балансам/курсам — coin-service;
// здесь только чтение + приведение формы и специфичная для приложения логика,
// которой нет в coin-service:
//   * фильтр сетей по is_wallet,
//   * спец-курс USDT=1 при usd,
//   * TON вверху списка,
//   * оверлеи на конфиг монеты: exchangeList (setFlagForAvailableExchange)
//     и address (setAddress).
//
// Прогрев состояния coin-service — `loadCoinServiceBalance()`.
import { ref, computed, watch } from "vue";
import { t } from "@/locales/i18n";
import formatCurrency from "@/utils/formatCurrency";
import store from "@/store";
import {
  CURRENCY_SYMBOLS as currencySymbols,
  fiatSymbol,
} from "@/shared/constants/currency";

const g = (name) => store.getters[`coinService/${name}`];

// Стабильный реактивный список конфигов монет, строится один раз из coin-service
const configCoins = ref([]);

// Оверлеи поверх конфигов монет:
// список доступных для обмена монет (exchangeList) и адреса депозита (address).
let exchangeOverlay = {}; // { [coinId]: Set<coinId> }
let addressOverlay = {}; // { [coinId]: { [network_id]: address } }

const resetOverlays = () => {
  exchangeOverlay = {};
  addressOverlay = {};
};

// Навешивает сохранённые оверлеи на свежепостроенный объект монеты.
const applyOverlays = (coin) => {
  if (exchangeOverlay[coin.id]) coin.exchangeList = exchangeOverlay[coin.id];
  const addrs = addressOverlay[coin.id];
  if (addrs) {
    coin.networks.forEach((n) => {
      if (addrs[n.network_id] != null) n.address = addrs[n.network_id];
    });
  }
  return coin;
};

const buildConfigCoins = (coinsObj) => {
  // coinService.coins (SET_COINS) уже содержит только активные монеты, но
  // guard оставляем как инвариант.
  return Object.values(coinsObj || {})
    .filter((c) => c.is_active !== false)
    .map((c) =>
      applyOverlays({
        ...c,
        networks: (c.networks ?? [])
          .filter((n) => n?.is_wallet)
          .map((n) => ({ ...n })),
      }),
    )
    .filter((c) => c.networks.length > 0);
};

// Перестраиваем при смене набора монет в coin-service (один раз на старте).
// Сохранённые оверлеи переприменяются в buildConfigCoins.
watch(
  () => store.state.coinService.coins,
  (coinsObj) => {
    // Логаут/сброс стора очищает набор монет
    if (!coinsObj || Object.keys(coinsObj).length === 0) resetOverlays();
    configCoins.value = buildConfigCoins(coinsObj);
  },
  { immediate: true },
);

// Курс монеты в текущей фиат-валюте со спец-правилом USDT=1 при usd.
const rateForFiat = (coin, fiat) => {
  if (!coin) return 0;
  if (String(coin).toLowerCase() === "usdt" && fiat === "usd") return 1;
  return g("getRateByCoin")(coin, fiat) ?? 0;
};

// setAddress / setFlagForAvailableExchange — специфичная для приложения логика
// поверх конфигов монет
const setAddress = (coinId, data) => {
  const coin = configCoins.value.find((a) => a.id === coinId);
  if (!coin) return;
  const addrs = addressOverlay[coinId] || (addressOverlay[coinId] = {});
  coin.networks = coin.networks.map((n) => {
    const networkData = data.find((a) => a.network_id === n.network_id);
    if (networkData) {
      n.address = networkData.address;
      addrs[n.network_id] = networkData.address;
    }
    return n;
  });
};

const setFlagForAvailableExchange = (pairsArr) => {
  const byId = {};
  const legacyToId = {};
  configCoins.value.forEach((coin) => {
    byId[coin.id] = new Set();
    legacyToId[coin.legacy_coin_id] = coin.id;
  });

  pairsArr.forEach((pairData) => {
    if (pairData.directions === "off") return;

    const baseId = legacyToId[pairData.base_coin_id];
    const quoteId = legacyToId[pairData.quote_coin_id];
    if (!baseId || !quoteId) return;

    if (pairData.directions === "both") {
      byId[baseId].add(quoteId);
      byId[quoteId].add(baseId);
    } else if (pairData.directions === "buy") {
      byId[baseId].add(quoteId);
    } else if (pairData.directions === "sell") {
      byId[quoteId].add(baseId);
    }
  });

  exchangeOverlay = byId;
  configCoins.value.forEach((coin) => {
    coin.exchangeList = byId[coin.id] || new Set();
  });
};

export function useCoins() {
  const preferredCurrency = computed(() => g("preferredFiat") || "usd");

  const CS = computed(() => fiatSymbol(preferredCurrency.value));

  const wrapCurrency = (value) => {
    const n = Number(value) || 0;
    if (preferredCurrency.value === "usd") {
      return `${CS.value}${n.toFixed(2)}`;
    }
    return `${n.toFixed(2)}${CS.value}`;
  };

  const allowedCurrencies = computed(() => [
    { title: t("profile|usd"), key: "usd" },
    { title: t("profile|rub"), key: "rub" },
    { title: t("profile|eur"), key: "eur" },
  ]);

  // Старая форма `coins`: массив { config, balances, fiatBalances, stats },
  // отсортированный по fiat-балансу кошелька, TON — вверху. `config` ссылается
  // на стабильные объекты из configCoins.
  const coins = computed(() => {
    const balancesByCoin = store.state.coinService.coinBalances || {};
    const fiat = preferredCurrency.value;

    // All-or-nothing гейт: пока курсы не загружены, getRateByCoin отдаёт
    // null/undefined, а rateForFiat коалесит в 0
    // Показываем монеты только когда известен хотя бы один курс.
    const ratesReady = configCoins.value.some(
      (c) => g("getRateByCoin")(c.id, fiat) != null,
    );
    if (!ratesReady) return [];

    let arr = configCoins.value.map((config) => {
      const balances = { ...(balancesByCoin[config.id] || {}) };
      const price = rateForFiat(config.id, fiat);

      const fiatBalances = { ...balances };
      Object.keys(fiatBalances).forEach((key) => {
        if (typeof fiatBalances[key] === "number") {
          fiatBalances[key] = fiatBalances[key] * price;
        }
      });

      return { config, balances, fiatBalances, stats: { rate: price } };
    });

    // Только монеты, по которым есть запись баланса (как раньше).
    arr = arr.filter((a) => Object.keys(a.fiatBalances).length !== 0);

    arr.sort(
      (a, b) =>
        (b.fiatBalances.wallet_balance || 0) -
        (a.fiatBalances.wallet_balance || 0),
    );

    const index = arr.findIndex((item) => item.config.id === "ton");
    if (index > -1) {
      const [tonObj] = arr.splice(index, 1);
      arr.unshift(tonObj);
    }

    return arr;
  });

  // Плоский список балансов с предформатированным wallet_balance
  const balances = computed(() =>
    Object.values(store.state.coinService.coinBalances || {}).map((b) => ({
      ...b,
      wallet_balance_formatted: Number(b.wallet_balance ?? 0).toLocaleString(
        "en-US",
        {
          maximumFractionDigits: 20,
          useGrouping: false,
        },
      ),
    })),
  );

  // Сырой ответ statsv2 (для потребителей, читающих
  // stats.data[coin][`${fiat}_rate`]).
  const stats = computed(() => store.state.coinService.rateResponse ?? {});

  // Значение монеты в фиат-строке.
  const cryptoInFiat = (coin, value, truncate) => {
    const price = rateForFiat(coin, preferredCurrency.value);
    return formatCurrency(
      (Number(value) || 0) * price,
      preferredCurrency.value,
      truncate,
    );
  };

  return {
    coins,
    configCoins,
    balances,
    stats,
    preferredCurrency,
    CS,
    wrapCurrency,
    currencySymbols,
    allowedCurrencies,
    cryptoInFiat,
    setAddress,
    setFlagForAvailableExchange,
  };
}
